import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, TrendingUp, AlertCircle, ArrowUpRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Admin analytics card — surfaces what users search for.
 * The `no_results` list is a direct content-gap signal for the FAQ.
 */
export default function AdminSearchAnalyticsCard() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState("30");
    const [loading, setLoading] = useState(true);

    async function load(d = days) {
        setLoading(true);
        try {
            const { data } = await api.get(`/search/analytics?days=${d}`);
            setData(data);
        } catch { setData(null); }
        finally { setLoading(false); }
    }

    useEffect(() => { load(days); }, [days]);

    const maxDay = Math.max(1, ...(data?.by_day || []).map((d) => d.count));

    return (
        <div className="border border-border bg-card rounded p-4 space-y-4" data-testid="admin-search-analytics-card">
            <div className="flex items-center gap-2 flex-wrap">
                <Search className="w-5 h-5 text-primary" />
                <div className="text-lg font-bold tracking-tight">Search analytics</div>
                <div className="ml-auto flex items-center gap-2">
                    <Select value={days} onValueChange={setDays}>
                        <SelectTrigger className="h-8 text-xs w-32" data-testid="analytics-window">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">Last 7 days</SelectItem>
                            <SelectItem value="30">Last 30 days</SelectItem>
                            <SelectItem value="90">Last 90 days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading && !data && <div className="text-sm text-muted-foreground">Loading…</div>}

            {data && data.total_searches === 0 && (
                <div className="text-sm text-muted-foreground py-4" data-testid="analytics-empty">
                    No searches recorded in the last {data.days} days yet. Once users start using the platform search bar, this dashboard will light up.
                </div>
            )}

            {data && data.total_searches > 0 && (
                <>
                    {/* KPI strip */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="border border-border rounded p-3">
                            <div className="overline">Total searches</div>
                            <div className="text-2xl font-extrabold mono" data-testid="analytics-total">
                                {data.total_searches.toLocaleString()}
                            </div>
                        </div>
                        <div className="border border-border rounded p-3">
                            <div className="overline">Unique queries</div>
                            <div className="text-2xl font-extrabold mono" data-testid="analytics-unique">
                                {data.unique_queries.toLocaleString()}
                            </div>
                        </div>
                        <div className="border border-border rounded p-3">
                            <div className="overline">Zero-result rate</div>
                            <div
                                className={`text-2xl font-extrabold mono ${data.no_results_pct >= 30 ? "text-danger" : data.no_results_pct >= 15 ? "text-warning" : "text-success"}`}
                                data-testid="analytics-noresults-pct"
                            >
                                {data.no_results_pct}%
                            </div>
                        </div>
                    </div>

                    {/* Sparkline */}
                    {data.by_day.length > 0 && (
                        <div>
                            <div className="overline mb-1">Daily volume</div>
                            <div className="flex items-end gap-0.5 h-16" data-testid="analytics-sparkline">
                                {data.by_day.map((d) => (
                                    <div
                                        key={d.date}
                                        className="flex-1 bg-primary/70 hover:bg-primary transition-colors rounded-t"
                                        style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: 2 }}
                                        title={`${d.date}: ${d.count} searches`}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                        {/* Top queries */}
                        <div>
                            <div className="overline mb-2 flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" /> Top queries
                            </div>
                            <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="analytics-top-queries">
                                {data.top_queries.length === 0 && (
                                    <div className="text-xs text-muted-foreground italic">No queries yet.</div>
                                )}
                                {data.top_queries.map((q) => (
                                    <div key={q.query} className="flex items-center justify-between text-xs py-1 px-2 border border-border/50 rounded hover:border-border">
                                        <span className="truncate mono">{q.query}</span>
                                        <span className="ml-2 shrink-0 flex items-center gap-2">
                                            {q.no_results > 0 && (
                                                <span className="text-danger text-[10px]" title={`${q.no_results} returned no results`}>
                                                    {q.no_results}× zero
                                                </span>
                                            )}
                                            <span className="font-bold mono">{q.count}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* No-result queries — content gap signal */}
                        <div>
                            <div className="overline mb-2 flex items-center gap-1 text-danger">
                                <AlertCircle className="w-3 h-3" /> Content gaps (no results)
                            </div>
                            <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="analytics-content-gaps">
                                {data.no_results_queries.length === 0 && (
                                    <div className="text-xs text-muted-foreground italic">
                                        🎉 Zero content gaps — every search returned results.
                                    </div>
                                )}
                                {data.no_results_queries.map((q) => (
                                    <div key={q.query} className="flex items-center justify-between text-xs py-1 px-2 border border-danger/30 bg-danger/5 rounded">
                                        <span className="truncate mono">{q.query}</span>
                                        <span className="font-bold mono ml-2 text-danger">{q.count}</span>
                                    </div>
                                ))}
                            </div>
                            {data.no_results_queries.length > 0 && (
                                <div className="mt-2 text-[10px] text-muted-foreground flex items-start gap-1">
                                    <ArrowUpRight className="w-3 h-3 shrink-0 mt-0.5" />
                                    Consider adding these as FAQ entries.
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
